import { useAppDispatch } from 'store/hooks';
import React from 'react';
import { MainLayout } from 'layouts/MainLayout';
import { Box, Button, Paper } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { actionExportsApi } from 'components/analytics/export/actionExportsApi';
import { ACTION_OPERATIONS_LIMIT, IActionOperation } from 'components/analytics/export/models';
import { ExportedDataTable } from 'components/analytics/export/tables/ExportedDataTable';
import { useInterval } from 'lib/hooks/useInterval';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { useSnackbar } from 'notistack';
import { MESSAGE_ACTION_EXPORT_ERROR, MESSAGE_ACTION_EXPORT_SUCCESS } from 'config/messages';
import logger from 'lib/logger';
import { ActionExportDrawer } from 'components/analytics/export/drawer/ActionExportDrawer';
import { ErrorLayout } from 'layouts/ErrorLayout';
import { LOCATIONS } from 'config/routes';
import { Row } from 'ui/Flex';
import { ExportAnalyticsDialog } from 'components/analytics/export/ExportAnalyticsDialog';
import { updateArrayItem } from 'lib/helpers';
import { getAllLocations } from 'store/locations/locationsSlice';

function filterPendingOperations(item: IActionOperation) {
    return item.status === OperationStatus.PENDING || item.status === OperationStatus.RUNNING;
}

function mergeOperations(oOne: IActionOperation[], oTwo: IActionOperation[]) {
    return oOne.map(item => {
        const newOperation = oTwo.find(operation => operation._id === item._id);
        if (newOperation) {
            return newOperation;
        }
        return item;
    });
}

const ExportDataPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const [exportOpen, setExportOpen] = React.useState(false);
    const [pendingOperations, setPendingOperations] = React.useState<IActionOperation[]>([]);
    const [isOperationsLoading, setOperationsLoading] = React.useState(false);
    const [exportOperations, setExportOperations] = React.useState<IActionOperation[]>([]);
    const [page, setPage] = React.useState(0);
    const [pages, setPages] = React.useState<(string | undefined)[]>([undefined]);
    const [endOfData, setEndOfData] = React.useState(false);
    const [operationsError, setOperationsError] = React.useState<string>();
    const [selectedOperationId, setSelectedOperationId] = React.useState<string>(undefined);

    useInterval(() => {
        if (pendingOperations.length) {
            const promises = pendingOperations.map(item => actionExportsApi.getOperation(item._id));
            Promise.all(promises)
                .then(results => {
                    const resultOperations = results.map(item => item.body);
                    const pending = resultOperations.filter(filterPendingOperations);
                    setPendingOperations(pending);
                    if (resultOperations.length - pending.length) {
                        enqueueSnackbar(MESSAGE_ACTION_EXPORT_SUCCESS, { variant: 'success' });
                    }
                    setExportOperations(current => mergeOperations(current, resultOperations));
                })
                .catch(e => {
                    logger.error(e.message);
                    enqueueSnackbar(MESSAGE_ACTION_EXPORT_ERROR, { variant: 'error' });
                });
        }
    }, 1500);

    React.useEffect(() => {
        dispatch(getAllLocations());
    }, [dispatch]);

    const selectedOperation = React.useMemo(() => {
        if (!selectedOperationId) {
            return undefined;
        }
        return exportOperations.find(item => item._id === selectedOperationId);
    }, [selectedOperationId, exportOperations]);

    const loadPage = React.useCallback(
        async (pageNumber: number) => {
            setOperationsLoading(true);
            try {
                const params = pages[pageNumber] ? { nextKey: pages[pageNumber] } : undefined;
                const res = await actionExportsApi.getOperations(params);
                const newItems = res.body.items;
                if (!res.body.page.nextKey) {
                    setEndOfData(true);
                }
                setExportOperations(current => {
                    if (pageNumber === 0) {
                        return newItems;
                    }
                    return [...current, ...newItems];
                });
                setPendingOperations(current => {
                    const newPending = newItems.filter(filterPendingOperations);
                    if (pageNumber === 0) {
                        return newPending;
                    }
                    return [...current, ...newPending];
                });
                setPages(current => updateArrayItem(current, pageNumber + 1, res.body.page.nextKey));
            } catch (e) {
                if (pageNumber === 0) {
                    setExportOperations([]);
                    setOperationsError(e.message);
                } else {
                    enqueueSnackbar(e.message || 'Failed to load exports', { variant: 'error' });
                }
            } finally {
                setOperationsLoading(false);
            }
        },
        [enqueueSnackbar, pages]
    );

    React.useEffect(() => {
        const needsLoad =
            (page === 0 && exportOperations.length === 0 && !operationsError && !endOfData) ||
            (!pages[page + 1] &&
                !endOfData &&
                exportOperations.length < pages.length * ACTION_OPERATIONS_LIMIT);
        if (needsLoad && !isOperationsLoading) {
            loadPage(page);
        }
    }, [page, exportOperations.length, pages, endOfData, isOperationsLoading, loadPage, operationsError]);

    const displayedOperations = React.useMemo(
        () => exportOperations.slice(page * ACTION_OPERATIONS_LIMIT, (page + 1) * ACTION_OPERATIONS_LIMIT),
        [exportOperations, page]
    );

    const count = React.useMemo(() => {
        if (endOfData) {
            return exportOperations.length;
        }
        return -1;
    }, [endOfData, exportOperations.length]);

    const handlePageChange = React.useCallback((_: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleOperationClick = React.useCallback((params: GridRowParams) => {
        setSelectedOperationId(params.row._id);
    }, []);
    const handleDrawerClose = React.useCallback(() => {
        setSelectedOperationId(undefined);
    }, []);
    const handleActionExportCreation = React.useCallback((exportOperation: IActionOperation) => {
        setPendingOperations(currentOperations => [...currentOperations, exportOperation]);
        setExportOperations(currentOperations => [exportOperation, ...currentOperations]);
        setPage(0);
    }, []);

    const handleExportClick = React.useCallback(() => {
        setExportOpen(true);
    }, []);

    const handleExportClose = React.useCallback(() => {
        setExportOpen(false);
    }, []);

    return (
        <MainLayout
            pageName="Export Data"
            pageDescription="Export data points on your customers' interactions with the app."
            noScroll
        >
            {!!operationsError && (
                <ErrorLayout title="Unexpected Error" fallbackUrl={LOCATIONS} fallbackTitle="Home" />
            )}
            {!operationsError && (
                <>
                    <Box height="100%" paddingBottom={14}>
                        <Box paddingBottom={1}>
                            <Row align="flex-end">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleExportClick}
                                    size="small"
                                >
                                    Create a new Export
                                </Button>
                            </Row>
                        </Box>
                        <Paper sx={{ height: '100%', display: 'flex' }}>
                            <ExportedDataTable
                                operations={displayedOperations}
                                loading={isOperationsLoading}
                                count={count}
                                page={page}
                                onPageChange={handlePageChange}
                                onRowClick={handleOperationClick}
                            />
                        </Paper>
                    </Box>
                    <ActionExportDrawer onClose={handleDrawerClose} operation={selectedOperation} />
                    <ExportAnalyticsDialog
                        open={exportOpen}
                        onCancel={handleExportClose}
                        onActionExportCreation={handleActionExportCreation}
                    />
                </>
            )}
        </MainLayout>
    );
};

export default ExportDataPage;
